import React, { Suspense } from 'react';

import { Switch, Redirect, Route } from 'react-router-dom';
import AuthRoute from 'components/AuthRoute';
import { isMobile } from 'helpers';
import TextLoader from 'components/TextLoader';
import AppLoader from 'components/AppLoader';
import SnackbarDisplayer from 'components/SnackbarDisplayer';

import useTrackers from 'providers/useTrackers';
import useRefreshOnResume from 'providers/useRefreshOnResume';

import Side from 'containers/Side/Side';
import useNavigationItems from 'containers/Sidebar/useNavigationItems';
import Providers from './Providers';

import WebDefaultLayout from './Layouts/DefaultLayout/WebDefaultLayout';
import Home from './Home';
import lazyWithRetry from './lazyWithRetry';

const Preferences = lazyWithRetry(() => import(/* webpackChunkName: 'preferences' */ './Preferences'));

const Menu = lazyWithRetry(() => import(/* webpackChunkName: 'mobile' */ './Menu'));
const Notifications = lazyWithRetry(() => import(/* webpackChunkName: 'mobile' */ './Notifications'));
const PostCreate = lazyWithRetry(() => import(/* webpackChunkName: 'mobile' */ './Post/Create'));
const PostEdit = lazyWithRetry(() => import(/* webpackChunkName: 'mobile' */ './Post/Edit'));
const ContactDirectory = lazyWithRetry(() => import(/* webpackChunkName: 'mobile' */ './ContactDirectory/ContactDirectory'));
const ManageContacts = lazyWithRetry(() => import(/* webpackChunkName: 'mobile' */ './ContactDirectory/ManageContacts'));
const ActivityEdit = lazyWithRetry(() => import(/* webpackChunkName: 'mobile' */ './Activity/Edit'));
const ActivityCreate = lazyWithRetry(() => import(/* webpackChunkName: 'mobile' */ './Activity/Create'));

const Post = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Post/Show'));
const NewsFeed = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './NewsFeed'));
const Groups = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Group/Groups'));
const Group = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Group/Group'));
const Feedback = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Feedback/Feedback'));
const Calendar = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Calendar'));
const Drive = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Drive'));
const Course = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Course'));
const Training = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Training'));
const Learning = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Learning/Learning'));
const LearningCourse = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Learning/Course'));
const LearningCourseVariantPreview = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Learning/CourseVariantPreview'));
const LearningLesson = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Learning/Lesson'));
const DocumentsFolders = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Documents/Folders'));
const DocumentsFolder = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Documents/Folder'));
const Event = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Event'));
const Record = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Record'));
const CompanyProfile = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './CompanyProfile'));
const Guidance = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Guidance'));
const Article = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Article'));
const Challenges = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Challenge/Challenges'));
const Challenge = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Challenge/Show'));
const Files = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Files'));
const Search = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Search'));
const Member = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Member'));
const Page = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Page'));
const KnowledgeHub = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './KnowledgeHub'));
const Subportals = lazyWithRetry(() => import(/* webpackPrefetch: true, webpackChunkName: 'secondary' */ './Subportals/Subportals'));

const Forgot = lazyWithRetry(() => import(/* webpackChunkName: 'auth' */ './Auth/Forgot'));
const Login = lazyWithRetry(() => import(/* webpackChunkName: 'auth' */ './Auth/Login'));
const Register = lazyWithRetry(() => import(/* webpackChunkName: 'auth' */ './Auth/Register'));
const Reset = lazyWithRetry(() => import(/* webpackChunkName: 'auth' */ './Auth/Reset'));
const Join = lazyWithRetry(() => import(/* webpackChunkName: 'auth' */ './Auth/Join'));
const Setup = lazyWithRetry(() => import(/* webpackChunkName: 'auth' */ './Auth/Setup'));

const PublicFeedbackForm = lazyWithRetry(() => import(/* webpackChunkName: 'public' */ './Public/FeedbackForm'));
const PublicFeedbackView = lazyWithRetry(() => import(/* webpackChunkName: 'public' */ './Public/PublicFeedbackView'));

const ManageMembers = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Members/Members'));
const ManageCalendar = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Calendar'));
const ManageDrive = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Drive'));
const ManageLearning = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Learning/Learning'));
const ManageLearningCourse = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Learning/Course'));
const ManageLearningCourseAdd = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Learning/AddCourse'));
const ManageLearningCourseVariantEdit = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Learning/EditCourseVariant'));
const ManageLearningAnalytics = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Learning/Analytics'));
const ManageDocumentsFolder = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Documents/Folders'));
const ManageDocumentsFolderAdd = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Documents/AddFolder'));
const ManageDocumentsFolderEdit = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Documents/EditFolder'));
const ManageTraining = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Training'));
const ManageSettings = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Settings/Settings'));
const ManageGroups = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Groups'));
const ManageChallenges = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Challenges'));
const ManagePages = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Pages/Pages'));
const ManageCollections = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Collections/Collections'));
const ManagePulse = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Pulse/Pulse'));
const ManagePosts = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Posts/Posts'));
const ManageFeedback = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Feedback/Feedback'));
const ManageFeedbackView = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Feedback/FeedbackShow'));
const ManageAnalytics = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Analytics'));
const ManageTranslations = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/Translations'));
const BestPractices = lazyWithRetry(() => import(/* webpackChunkName: 'admin' */ './Manage/BestPractices'));

const Superpowers = React.lazy(() => import(/* webpackChunkName: 'superpowers' */ './Superpowers/Superpowers'));

const publicRoutes = [
  { path: '/public/feedback/:feedbackTypeId', component: PublicFeedbackForm },
  { path: '/public/feedback-sent/:feedbackId', component: PublicFeedbackView },
];

const CallbackUrl = ({ children }) => <>{children}</>;

const unauthRoutes = [
  { path: '/join/:invitationToken?', component: Join },
  { path: '/login', component: Login },
  { path: '/forgot', component: Forgot },
  { path: '/reset/:token', component: Reset },
  { path: '/register/:invitationToken', component: Join },
  { path: '/signup', component: Register },
];

// Legacy Pulse is gone; old links and push notifications still open its page
const RedirectHome = () => <Redirect to="/" />;

export const workspaceRoutes = [

  { path: '/oauth/callback', component: CallbackUrl },

  { path: '/', component: Home, exact: true },

  { path: '/preferences', component: Preferences },

  { path: '/challenges/:id', component: Challenge },
  { path: '/challenges', component: Challenges },
  { path: '/calendar/:status?', component: Calendar },
  { path: '/drive/:type?', component: Drive },
  { path: '/training/:status?', component: Training },
  {
    path: [
      '/e-learning/courses',
      '/e-learning/courses/completed',
      '/e-learning/team',
    ],
    component: Learning,
  },
  { path: '/e-learning/course/:courseId/variant/:courseVariantId/preview', component: LearningCourseVariantPreview },
  { path: '/e-learning/course/:id', component: LearningCourse },
  { path: '/e-learning/lesson/:id', component: LearningLesson },
  { path: '/documents/', component: DocumentsFolders, exact: true },
  { path: '/documents/folder/:id', component: DocumentsFolder },
  { path: '/guidance/:filter?', component: Guidance },
  { path: '/article/:id', component: Article },
  { path: '/profile', component: CompanyProfile },
  { path: '/feedback/pulse', component: RedirectHome },
  { path: '/feedback', component: Feedback },
  { path: '/files', component: Files },
  { path: '/subportals', component: Subportals },

  { path: '/groups/:id', component: Group },
  { path: '/groups', component: Groups },
  { path: '/messages/:id?', component: NewsFeed },

  { path: '/post/create', component: isMobile ? PostCreate : null },
  { path: '/activity/create', component: isMobile ? ActivityCreate : null },
  { path: '/activity/:id/edit', component: isMobile ? ActivityEdit : null },
  { path: '/post/:id/edit', component: isMobile ? PostEdit : null },
  { path: '/post/:id', component: Post },
  { path: '/page/:id', component: Page },
  { path: '/knowledge-hub', component: KnowledgeHub },

  { path: '/menu', component: isMobile ? Menu : null },
  { path: '/notifications', component: isMobile ? Notifications : null },
  { path: '/directory', component: isMobile ? ContactDirectory : null },
  { path: '/contacts', component: isMobile ? ManageContacts : null },
  { path: '/member/:id', component: Member },

  { path: '/event/:id', component: Event },
  { path: '/course/:id', component: Course },
  { path: '/record/:id', component: Record },

  { path: '/search/:filter?', component: Search },
];

const ManageGenericRedirect = () => {
  const { adminItems, memberItems } = useNavigationItems();

  if (adminItems.length) {
    return <Redirect to={adminItems[0].to} />;
  }

  return <Redirect to={memberItems[0].to} />;
};

const managementRoutes = process.env.REACT_APP_BUILD_FOR !== 'mobile' ? [
  { path: '/manage/analytics', component: ManageAnalytics },
  { path: '/manage/calendar/:status?', component: ManageCalendar },
  { path: '/manage/drive/:type?', component: ManageDrive },
  { path: ['/manage/e-learning', '/manage/e-learning/training-plan'], exact: true, component: ManageLearning },
  { path: ['/manage/e-learning/course/add', '/manage/e-learning/course/:courseId/variant/add'], exact: true, component: ManageLearningCourseAdd },
  { path: '/manage/e-learning/course/:courseId/:view?', exact: true, component: ManageLearningCourse },
  { path: '/manage/e-learning/course/:courseId/variant/:courseVariantId/edit/:edit?', exact: true, component: ManageLearningCourseVariantEdit },
  { path: '/manage/e-learning/analytics/:filter?', exact: true, component: ManageLearningAnalytics },
  { path: '/manage/documents', exact: true, component: ManageDocumentsFolder },
  { path: ['/manage/documents/folder/add', '/manage/documents/folder/:folderId/variant/add'], exact: true, component: ManageDocumentsFolderAdd },
  { path: '/manage/documents/folder/:folderId/variant/:folderVariantId/edit/:edit?', exact: true, component: ManageDocumentsFolderEdit },
  { path: '/manage/training/:status?', component: ManageTraining },
  { path: '/manage/members/:filter?', component: ManageMembers },
  { path: '/manage/challenges/:filter?', component: ManageChallenges },
  { path: ['/manage/pages', '/manage/knowledge-hub'], component: ManagePages },
  { path: '/manage/collections', component: ManageCollections },
  { path: '/manage/pulse-surveys', component: ManagePulse },
  { path: '/manage/posts/:tab?', component: ManagePosts },

  { path: '/manage/feedback/:feedbackId', component: ManageFeedbackView },
  {
    path: [
      '/manage/feedback',
      '/manage/feedback-types',
      '/manage/feedback-types/:feedbackTypeId',
      '/manage/feedback-types/:feedbackTypeId/edit',
    ],
    exact: true,
    component: ManageFeedback },

  { path: '/manage/groups', component: ManageGroups },
  { path: '/manage/settings', component: ManageSettings },
  { path: '/manage/translations', component: ManageTranslations },
  { path: '/manage/practices/:filter?', component: BestPractices },
  { path: '/manage', component: ManageGenericRedirect },
] : [];

const DefaultLayoutForWeb = process.env.REACT_APP_BUILD_FOR !== 'mobile' ? WebDefaultLayout : React.Fragment;

const superpowerRoutes = [
  { path: '/superpowers', component: Superpowers },
];

// Signed in, but still part of registration, so it keeps the auth screen look instead of the app layout
const setupRoutes = [
  { path: '/setup', component: Setup },
];

const authRoutes = [
  ...managementRoutes,
  ...workspaceRoutes,
];

const App = () => {

  useTrackers();
  useRefreshOnResume();

  return (
    <Suspense fallback={<AppLoader delay={300} />}>

      <Switch>

        <Route exact path={['/register', '/register-evp']} render={({ location }) => <Redirect to={{ ...location, pathname: '/signup' }} />} />

        {unauthRoutes.map(route => <AuthRoute key={route.path} {...route} auth={false} />)}

        {publicRoutes.map(route => <Route key={route.path} {...route} auth={null} />)}

        {superpowerRoutes.map(route => <AuthRoute key={route.path} {...route} />)}

        {setupRoutes.map(route => <AuthRoute key={route.path} {...route} />)}

        <DefaultLayoutForWeb>
          <Suspense fallback={<TextLoader delay={300} />}>

            <Switch>
              {authRoutes.map(route => <AuthRoute key={route.path} {...route} />)}
              <Redirect to="/" />
            </Switch>

          </Suspense>
        </DefaultLayoutForWeb>

      </Switch>

      <Side />

      <SnackbarDisplayer />

    </Suspense>
  );
};

const AppWithProviders = () => (
  <Suspense fallback={null}>
    <Providers>
      <App />
    </Providers>
  </Suspense>
);

export default AppWithProviders;
